// lib/slides/brandResearch.ts
import type { SlideBrandResearch } from '#types/types';

/** A review researches the brand first when the deck has no brief to stand on: none yet, or one a brand correction voided. */
export const needsBrandResearch = (research: SlideBrandResearch | undefined): boolean =>
  !research || research.status === 'invalidated';

export const researchWebsiteHost = (url?: string | null): string | null => {
  try { return new URL(url ?? '').hostname.toLowerCase().replace(/^www\./, '') || null; }
  catch { return null; }
};

export function selectResearchBrand<T extends { websiteUrl: string | null }>(brands: T[], websiteUrl?: string): T | null {
  const host = researchWebsiteHost(websiteUrl);
  if (host) {
    const matching = brands.filter((brand) => researchWebsiteHost(brand.websiteUrl) === host);
    return matching.length === 1 ? matching[0]! : null;
  }
  return brands.length === 1 ? brands[0]! : null;
}

export const RESEARCH_COMMANDS = new Set([
  'SLIDES_START_BRAND_RESEARCH', 'SLIDES_END_BRAND_RESEARCH',
  'SLIDES_READ_PAGE', 'SLIDES_IDENTIFY_BRAND', 'SLIDES_WEB_SEARCH',
  'SLIDES_READ_BRAND_CONTEXT', 'SLIDES_COLLECT_BRAND_REFERENCES', 'SLIDES_SAVE_BRAND_RESEARCH',
]);

export function describeBrandResearch(research?: SlideBrandResearch): string {
  if (!research) return '';
  if (research.status === 'invalidated') return `Brand corrected to ${research.brand.name}. Previous brand research is invalid and must not influence this deck.`;
  return [
    `BRAND RESEARCH: ${research.brand.name}, researched ${research.researchedAt}, ${research.status}.`,
    'Evidence and visual inspiration only. Active guidelines and project constraints take priority. References never establish the submitted design’s compliance.',
    research.brief,
    ...research.sources.map((s) => `${s.kind}: ${s.title} ${s.url}${s.publishedDate ? ` (${s.publishedDate})` : ''}`),
    ...research.references.map((r) => `Vetted visual reference: ${r.description} | ${r.assetUrl} | source ${r.sourceUrl}`),
  ].join('\n');
}
